import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { ActionModel } from '../../models/action.model';
import { ConditionModel } from '../../models/condition.model';
import { DeviceModel } from '../../models/device.model';

const INIT_ZONES_LEN = 8;
const INIT_ZONES: any = [
  { name: 'Zone 1', isOn: false, enabled: true, index: 0, currentTime: 0 },
  { name: 'Zone 2', isOn: false, enabled: true, index: 1, currentTime: 0 },
  { name: 'Zone 3', isOn: false, enabled: true, index: 2, currentTime: 0 },
  { name: 'Zone 4', isOn: false, enabled: true, index: 3, currentTime: 0 },
  { name: 'Zone 5', isOn: false, enabled: true, index: 4, currentTime: 0 },
  { name: 'Zone 6', isOn: false, enabled: true, index: 5, currentTime: 0 },
  { name: 'Zone 7', isOn: false, enabled: true, index: 6, currentTime: 0 },
  { name: 'Zone 8', isOn: false, enabled: true, index: 7, currentTime: 0 },
  { name: 'Zone 9', isOn: false, enabled: true, index: 8, currentTime: 0 },
  { name: 'Zone 10', isOn: false, enabled: true, index: 9, currentTime: 0 },
  { name: 'Zone 11', isOn: false, enabled: true, index: 10, currentTime: 0 },
  { name: 'Zone 12', isOn: false, enabled: true, index: 11, currentTime: 0 },
  { name: 'Zone 13', isOn: false, enabled: true, index: 12, currentTime: 0 },
  { name: 'Zone 14', isOn: false, enabled: true, index: 13, currentTime: 0 },
  { name: 'Zone 15', isOn: false, enabled: true, index: 14, currentTime: 0 },
  { name: 'Zone 16', isOn: false, enabled: true, index: 15, currentTime: 0 }
];

const INIT_PROGRAMS: any = [
  { name: 'Default', isOn: false, timers: [], index: 0 },
  { name: 'Program A', isOn: false, timers: [], index: 1 },
  { name: 'Program B', isOn: false, timers: [], index: 2 },
  { name: 'Program C', isOn: false, timers: [], index: 3 },
  { name: 'Program D', isOn: false, timers: [], index: 4 }
];

const INIT_PERCENT_ADJUST: number = 0;
const INIT_IS_ENABLED: boolean = false;
const CACHE_TIME: number = 3600;
const SETTLE_TIME = 2 * 1000;

export class OnOffArrayServerPlugin extends SmartenitPlugin {
  private _zones: any = _.cloneDeep(INIT_ZONES);
  private _programs: any = _.cloneDeep(INIT_PROGRAMS);
  private _percentAdjust: number = INIT_PERCENT_ADJUST;
  private _isEnabled: boolean = INIT_IS_ENABLED;
  private _zonesLen: number = INIT_ZONES_LEN;
  private _supportsBudgetAndRunningTimer: boolean = true;

  private days: any = [
    { value: 1, name: 'Monday', shortName: 'Mon', enabled: false },
    { value: 2, name: 'Tuesday', shortName: 'Tue', enabled: false },
    { value: 3, name: 'Wednesday', shortName: 'Wed', enabled: false },
    { value: 4, name: 'Thursday', shortName: 'Thu', enabled: false },
    { value: 5, name: 'Friday', shortName: 'Fri', enabled: false },
    { value: 6, name: 'Saturday', shortName: 'Sat', enabled: false },
    { value: 0, name: 'Sunday', shortName: 'Sun', enabled: false }
  ];

  get isEnabled(): boolean {
    return this._isEnabled;
  }

  get currentRunningProgram(): any {
    return this._programs.find((item: any, index: number) => {
      return item.isOn === true;
    });
  }

  get percentAdjust(): number {
    return this._percentAdjust;
  }

  get zones(): any {
    return this._zones;
  }

  get programs(): any {
    return this._programs;
  }

  get zonesLen(): any {
    return this._zonesLen;
  }

  get supportsBudgetAndRunningTimer(): boolean {
    return this._supportsBudgetAndRunningTimer;
  }

  /**
   * supportsBudgetAndRunningTimer
   */
  public updatesupportsBudgetAndRunningTimer(value: boolean): any {
    this._supportsBudgetAndRunningTimer = value;
  }

  protected _onRainDelayChange: Subject<boolean> = new Subject<boolean>();
  get onRainDelayChange(): Observable<boolean> {
    return this._onRainDelayChange.asObservable();
  }

  public onInit() {
    this.getCacheValuesWithDefaults(true);
  }

  public getReportableAttrs() {
    return ['Mode', 'State'];
  }

  public setProgramName(programIndex: number, programName: string) {
    this._programs = this.programs.map((program: any, idx: number) => {
      if (programIndex === idx) {
        program.name = programName;
      }
      return program;
    });

    this.setCache('programs', this._programs, CACHE_TIME);
    this._onUpdate.next({ programs: this.programs });
  }

  public getZoneName(zoneIdx: number) {
    if (zoneIdx < this._zonesLen) {
      this.device.executeMethod(
        this.componentId,
        this.processorName,
        'GetName',
        { ZoneID: zoneIdx + 1 },
        false
      );
    }
  }

  public setZoneName(zoneIndex: number, zoneName: string) {
    this._zones = this.zones.map((zone: any, idx: number) => {
      if (zoneIndex === idx) {
        zone.name = zoneName;
      }
      return zone;
    });
    this.setCache('zones', this._zones, CACHE_TIME);
    this._onUpdate.next({ zones: this.zones });
  }

  public setZoneNames(
    zonesInfo: Array<{ zoneIndex: number; zoneName: string }>
  ) {
    const changedZonesInfo = zonesInfo.filter(({ zoneIndex, zoneName }) => {
      const zone = this.zones.find((z: any) => z.index === zoneIndex);
      return zone && zone.name !== zoneName;
    });

    changedZonesInfo.forEach(({ zoneIndex, zoneName }) => {
      this.setZoneName(zoneIndex, zoneName);
    });
  }

  public executeSetZoneName(zoneIndex: number, zoneName: string) {
    this.device.executeMethod(this.componentId, this.processorName, 'SetName', {
      ZoneID: zoneIndex + 1,
      ZoneName: zoneName
    });
  }

  public updateDeviceZoneInfo(
    zoneIndex: number,
    zoneName: string
  ): Observable<any> {
    if (
      this.device &&
      (this.device.meta === undefined || this.device.meta === null)
    ) {
      this.device.meta = {};
    }

    this.device.meta.zones = this._zones;

    const existingComponent = this.device.getComponent(
      (zoneIndex + 1).toString()
    );
    if (existingComponent) {
      existingComponent.name = zoneName;
    }

    return this.device.save();
  }

  public updateDeviceZonesInfo(
    zonesInfo: Array<{ zoneIndex: number; zoneName: string }>
  ): Observable<any> {
    if (
      this.device &&
      (this.device.meta === undefined || this.device.meta === null)
    ) {
      this.device.meta = {};
    }

    this.device.meta.zones = this._zones;

    return this.device.save();
  }

  public setPump(zoneIndexes: number[], saveAttribute: boolean = true) {
    this._zones = this.zones.map((zone: any) => {
      zone.isPump = zoneIndexes.indexOf(zone.index) > -1;
      return zone;
    });

    this.setCache('zones', this._zones, CACHE_TIME);
    this._onUpdate.next({ zones: this.zones });

    if (saveAttribute) {
      const value = {
        value: zoneIndexes.map((x) => Math.pow(2, x)).reduce((x, a) => x + a, 0)
      };
      this.device
        .setAttribute(
          this.componentId,
          this.processorName,
          'PumpMode',
          value,
          false
        )
        .subscribe(() => {
          this.getPump(true);
        });
    }
  }

  public getPump(delay: boolean = false) {
    if (delay) {
      setTimeout(() => {
        this.device
          .getAttribute(
            this.componentId,
            this.processorName,
            'PumpMode',
            undefined,
            'cached=false'
          )
          .subscribe();
      }, SETTLE_TIME);
    } else {
      this.device
        .getAttribute(
          this.componentId,
          this.processorName,
          'PumpMode',
          undefined,
          'cached=false'
        )
        .subscribe();
    }
  }

  public updatePumpMode(pumpMode: any) {
    const binaryPumpmode = pumpMode
      .toString(2)
      .split('')
      .reverse();
    this.setPump(
      binaryPumpmode.reduce((z: any[], p: number, i: number) => {
        if (p == 1) {
          z = z.concat(i);
        }
        return z;
      }, []),
      false
    );
  }

  public readMode(delay: boolean = false) {
    if (delay === true) {
      setTimeout(() => {
        this.device
          .getAttribute(
            this.componentId,
            this.processorName,
            'Mode',
            undefined,
            'cached=false'
          )
          .subscribe(() => { });
      }, 500);
    } else {
      this.device
        .getAttribute(
          this.componentId,
          this.processorName,
          'Mode',
          undefined,
          'cached=false'
        )
        .subscribe(() => { });
    }
  }

  private getCacheValuesWithDefaults(useDefaultValues: boolean = false) {
    const cacheValues = [
      this.getCache('zones'),
      this.getCache('percentAdjust'),
      this.getCache('programs'),
      this.getCache('isEnabled'),
      this.getCache('zonesLen'),
      this.getCache()
    ];

    Observable.forkJoin(cacheValues).subscribe((array) => {
      if (array && array[0] != null) {
        this._zones = array[0];
      } else if (_.get(this.device, 'meta.zones')) {
        this._zones = this.device.meta.zones;
        this.setCache('zones', this._zones, CACHE_TIME);
      } else if (useDefaultValues) {
        this._zones = INIT_ZONES;
      }

      if (array && array[1] != null) {
        this._percentAdjust = array[1];
      } else if (useDefaultValues) {
        this._percentAdjust = INIT_PERCENT_ADJUST;
      }

      if (array && array[2] != null) {
        this._programs = array[2];
      } else if (_.get(this.device, 'meta.programs')) {
        const metaPrograms = _.get(this.device, 'meta.programs', []);
        this._programs = INIT_PROGRAMS.map((program: any) => {
          const metaProgram = metaPrograms.find(
            (p: any) => p.index == program.index
          );
          if (metaProgram) {
            return Object.assign({}, program, metaProgram);
          } else {
            return program;
          }
        });
      } else if (useDefaultValues) {
        this._programs = INIT_PROGRAMS;
      }

      if (array && array[3] != null) {
        this._isEnabled = array[3];
      } else if (useDefaultValues) {
        this._isEnabled = INIT_IS_ENABLED;
      }

      if (array && array[4] != null) {
        this._zonesLen = array[4];
      } else if (useDefaultValues) {
        this._zonesLen = INIT_ZONES_LEN;
      }

      if (array && array[5] != null) {
        this.state = array[5];
      } else if (useDefaultValues) {
        this.state = {};
      }

      this._onUpdate.next({
        isEnabled: this.isEnabled,
        percentAdjust: this.percentAdjust,
        programs: this.programs,
        state: this.state,
        zones: this.zones
      });
    });
  }

  public getState(cachedState: boolean = false, delay: boolean = false) {
    if (cachedState) {
      this.getCacheValuesWithDefaults();
    }

    if (delay === true) {
      setTimeout(() => {
        this.device
          .getAttribute(
            this.componentId,
            this.processorName,
            'State',
            undefined,
            'cached=false'
          )
          .subscribe();
        this.getArraySize();
      }, 500);
    } else {
      this.device
        .getAttribute(
          this.componentId,
          this.processorName,
          'State',
          undefined,
          'cached=false'
        )
        .subscribe();
      this.getArraySize();
    }
  }

  public getArraySize() {
    if (this.device && this.device.meta && this.device.meta.arraySize) {
      this.updateArraySize(this.device.meta.arraySize);
    } else {
      this.device
        .getAttribute(
          this.componentId,
          this.processorName,
          'ArraySize',
          undefined,
          `cacheTTL=${CACHE_TIME}`
        )
        .subscribe((response) => {
          if (
            response &&
            response.apiCache &&
            response.data &&
            response.data.length &&
            response.data[0].v
          ) {
            const value = response.data[0].v.value;
            this.updateArraySize(value);
            this.setArraySize(value);
          }
        });
    }
  }

  public setArraySize(arraySize: any) {
    if (!this.device || !this.device.meta || !this.device.meta.arraySize) {
      if (
        this.device &&
        (this.device.meta === undefined || this.device.meta === null)
      ) {
        this.device.meta = {};
      }
      this.device.meta.arraySize = arraySize;
      this.device.save().subscribe();
    }
  }

  public updateArraySize(arraySize: any) {
    if (this._zonesLen !== arraySize || this._zones.length !== arraySize) {
      this.getZoneNames();
      this._zones = this._zones.slice(0, arraySize);
      this._zonesLen = arraySize;
      this.setCache('zonesLen', this._zonesLen, CACHE_TIME);
      this.setCache('zones', this._zones, CACHE_TIME);
      this._onUpdate.next({ zonesLen: this._zonesLen });
      this._onUpdate.next({ zones: this.zones });
    }
  }

  public getZoneNames() {
    if (this.device && this.device.meta && this.device.meta.zones) {
      this._zones = this.device.meta.zones;
      this._onUpdate.next({ zones: this._zones });
    } else {
      if (this.device && (this.device.meta === undefined || this.device.meta === null)) {
        this.device.meta = {};
      }
      this.device.meta.zones = this._zones;
      this.device.save();
      this._onUpdate.next({ zones: this._zones });
    }
  }

  public turnOnProgram(programIndex: number) {
    const program = this.programs[programIndex] || {};

    this._programs = this._programs.map((item: any, index: number) => {
      item.isOn = false;
      return item;
    });

    if (this._programs[programIndex]) {
      this._programs[programIndex].isOn = true;
    }

    this._onUpdate.next({ programs: this.programs });

    const method = 'ProgramOn';
    const payload = { ProgramID: programIndex };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false
    );
    this.readMode(true);
    this.getState();
  }

  public turnOffProgram(programIndex: number) {
    const program = this.programs[programIndex] || {};

    this._programs = this._programs.map((item: any, index: number) => {
      item.isOn = false;
      return item;
    });

    if (this._programs[programIndex]) {
      this._programs[programIndex].isOn = true;
    }

    this._onUpdate.next({ programs: this.programs });

    const method = 'ProgramOff';
    const payload = { ProgramID: programIndex };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false
    );
    this.readMode(true);
    this.getState();
  }

  public toggleProgram(programIndex: number) {
    const program = this.programs[programIndex] || {};

    this._programs = this._programs.map((item: any, index: number) => {
      if (index! - programIndex) {
        item.isOn = false;
      }
      return item;
    });

    if (this._programs[programIndex]) {
      this._programs[programIndex].isOn = !this._programs[programIndex].isOn;
    }

    this.setCache('programs', this._programs, CACHE_TIME);
    this._onUpdate.next({ programs: this.programs });

    const method = program.isOn ? 'ProgramOn' : 'ProgramOff';
    const payload = { ProgramID: programIndex };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false
    );
    if (method === 'ProgramOn') {
      if (this._supportsBudgetAndRunningTimer) {
        setTimeout(() => {
          this.getCurrentRunningTimer();
        }, SETTLE_TIME);
      }
      // this.device.getStatus(this.componentId, this.processorName, {}, 'cached=false');
    } else {
      this._programs = this._programs.map((item: any, index: number) => {
        item.isOn = false;
        return item;
      });
      this._zones = this._zones.map((item: any, index: number) => {
        item.isOn = false;
        return item;
      });
    }

    setTimeout(() => {
      this.getState();
      this.readMode();
    }, SETTLE_TIME);
  }

  public toggleZone(zoneIndex: number) {
    const zone = this._zones[zoneIndex] || {};

    if (this._zones[zoneIndex]) {
      this._zones[zoneIndex].isOn = !this._zones[zoneIndex].isOn;
    }

    this._onUpdate.next({ zones: this.zones });

    const method = zone.isOn ? 'On' : 'Off';
    const payload = { ZoneID: zoneIndex + 1 };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false
    );
    if (method === 'On') {
      if (this._supportsBudgetAndRunningTimer) {
        setTimeout(() => {
          this.getCurrentRunningTimer();
        }, SETTLE_TIME);
      }
      // this.device.getStatus(this.componentId, this.processorName, {}, 'cached=false');
    } else {
      this._programs = this._programs.map((item: any, index: number) => {
        item.isOn = false;
        return item;
      });
      this._zones = this._zones.map((item: any, index: number) => {
        item.isOn = false;
        return item;
      });
    }

    setTimeout(() => {
      this.getState();
      this.readMode();
    }, SETTLE_TIME);
  }

  public getProgramTimers(programIndex: number) {
    const method = 'GetTimers';
    const payload = { TimerBankID: programIndex };

    if (programIndex !== 5) {
      if (this.device && this.device.meta && this.device.meta.programs) {
        const metaProgram: any = this.device.meta.programs.find(
          (p: any) => p.index == programIndex
        );
        if (
          metaProgram &&
          metaProgram.timers &&
          metaProgram.timers.length > 0
        ) {
          this._programs = this._programs.map((program: any) => {
            if (program.index == programIndex) {
              return Object.assign({}, program, {
                timers: metaProgram.timers
              });
            } else {
              return program;
            }
          });
          this.setCache('programs', this._programs, CACHE_TIME);
          this._onUpdate.next({ programs: this._programs });
        } else {
          this.device.executeMethod(
            this.componentId,
            this.processorName,
            method,
            payload,
            false
          );
          this.getState();
        }
      } else {
        this.device.executeMethod(
          this.componentId,
          this.processorName,
          method,
          payload,
          false
        );
        this.getState();
      }
    } else {
      this.device.executeMethod(
        this.componentId,
        this.processorName,
        method,
        payload,
        false,
        undefined,
        'cached=false'
      );
      this.getState();
    }
  }

  public getCurrentRunningTimer(sendDirect?: boolean) {
    const method = 'GetTimers';
    const payload = { TimerBankID: 5 };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false,
      null,
      'cached=false'
    );
  }

  public setProgramTimers(programIndex: number, timerValues: any) {
    const program = this._programs[programIndex];
    const programName = program.name;
    const method = 'SetTimers';

    program.timers = timerValues;
    this.setCache('programs', this._programs, CACHE_TIME);

    const timerValuesSecs = timerValues.map((timer: any) => {
      return parseInt(timer) * 60;
    });

    const payload = {
      ArrayOfTimerValues: timerValuesSecs,
      TimerBankID: programIndex
    };

    this.device.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      false
    );

    this.updateDeviceProgramTimers(
      programIndex,
      timerValues,
      program
    ).subscribe(() => {
      setTimeout(() => {
        this.getProgramTimers(programIndex);
      }, SETTLE_TIME);
    });
  }

  public updateDeviceProgramTimers(
    programIndex: number,
    timerValues: any,
    existingProgram: any
  ) {
    if (
      this.device &&
      (this.device.meta === undefined || this.device.meta === null)
    ) {
      this.device.meta = {};
    }

    const programs: any = _.get(this.device, 'meta.programs');
    if (programs) {
      const program: any = programs.find((p: any) => p.index == programIndex);
      if (program) {
        program.timers = timerValues;
      } else {
        existingProgram.index = programIndex;
        programs.push(existingProgram);
      }
    } else {
      _.set(this.device, 'meta.programs', [existingProgram]);
    }

    return this.device.save();
  }

  public toggleDeviceEnable() {
    if (this._isEnabled) {
      this.disableDevice();
    } else {
      this.enableDevice();
    }
  }

  public disableDevice() {
    if (this.state.mode === undefined) {
      this._onUpdate.next({ isEnabled: false });
      return Observable.of(false);
    }

    const newMode = this.inhibitOn(this.state.mode);

    const params = { value: newMode };

    this.device
      .setAttribute(this.componentId, this.processorName, 'Mode', params)
      .subscribe(() => {
        this.readMode(true);
      });
  }

  public enableDevice() {
    if (this.state.mode === undefined) {
      this._onUpdate.next({ isEnabled: false });
      return Observable.of(false);
    }

    const newMode = this.inhibitOff(this.state.mode);

    const params = { value: newMode };

    this.device
      .setAttribute(this.componentId, this.processorName, 'Mode', params)
      .subscribe(() => {
        this.readMode(true);
      });
  }

  private inhibitOn(mode: any) {
    const ihibitMask = 1 << 8;
    return mode | ihibitMask;
  }

  private inhibitOff(mode: any) {
    const ihibitMask = 1 << 8;
    return mode & ~ihibitMask;
  }

  private setProgramInMode(programIndex: number): Observable<any> {
    if (this.state.mode === undefined) {
      return Observable.of(true);
    }

    const newMode = this.setProgramId(this.state.mode, programIndex);
    const params = { value: newMode };

    return this.device.setAttribute(
      this.componentId,
      this.processorName,
      'Mode',
      params
    );
  }

  private setProgramId(mode: any, programIndex: number): any {
    const timerMask = 287;
    const shiftLeft = programIndex << 5;
    const cleanedMode = mode & timerMask;
    const newMode = cleanedMode | shiftLeft;

    return newMode;
  }

  private getProgramIdFromMode(mode: any) {
    const shiftRight = mode >> 5;
    return shiftRight & 7;
  }

  private getDeviceisEnabled(mode: any) {
    return mode & 256 ? true : false;
  }

  private getPercentAdjust(mode: any) {
    return (mode & 31) * 10;
  }

  public processZonesResponse(bitmask: any, zones: any) {
    return zones.map((zone: any, idx: number) => {
      const shiftRight = 1 << idx;

      if (bitmask & shiftRight) {
        zone.isOn = true;
      } else {
        zone.isOn = false;
      }

      return zone;
    });
  }

  public getStatusPayload(): any {
    return [
      'Mode',
      'State',
      'PumpMode'
    ];
  }

  public getStatus(
    context?: string,
    force?: boolean,
    asObservable?: boolean,
    payload?: any,
    sendDirect: boolean = false
  ) {
    if (payload && Object.keys(payload).length > 0) {
      payload = Object.assign(this.getStatusPayload(), payload);
    } else {
      payload = this.getStatusPayload();
    }

    this.device.getStatus(
      this.componentId,
      this.processorName,
      payload,
      'cached=false',
      sendDirect
    );
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;
    const response = message && message.data && message.data.response;

    if (attribute) {
      if (attribute.toLowerCase() === 'state') {
        const bitmask = response.value;
        this._zones = this.processZonesResponse(bitmask, this.zones);

        this.setCache('zones', this._zones, CACHE_TIME, undefined, timestamp);

        this._onUpdate.next({ zones: this.zones });
      } else if (attribute === 'GetNameResponse') {
        const zoneID = response.value.ZoneID;
        const zoneIndex = zoneID - 1;
        const zoneName = response.value.ZoneName;

        if (!zoneName) {
          if (
            this.device &&
            this.device.meta &&
            this.device.meta.zones &&
            this.device.meta.zones[zoneIndex]
          ) {
            this._zones[zoneIndex].name = this.device.meta.zones[zoneIndex].name;
          } else {
            this._zones[zoneIndex].name = 'Zone ' + zoneID;
          }
        } else {
          this._zones[zoneIndex].name = zoneName;
        }

        this.setCache('zones', this._zones, CACHE_TIME, undefined, timestamp);

        this._onUpdate.next({ zones: this.zones });
      } else if (attribute == 'GetTimersResponse') {
        const programId = response.value.TimerBankID;

        if (programId != 5) {
          const timersInSecs = response.value.ArrayOfTimerValues;
          const timersInMins =
            timersInSecs &&
            timersInSecs.map((timer: any) => {
              return Math.round(timer / 60);
            });
          this._programs[programId].timers =
            Array.isArray(timersInSecs) && timersInSecs.length > 0
              ? timersInMins
              : this._programs[programId].timers;

          this.setCache(
            'programs',
            this._programs,
            CACHE_TIME,
            undefined,
            timestamp
          );
          this._onUpdate.next({ programs: this.programs });
        } else {
          const currentTimers: any = response.value.ArrayOfTimerValues;

          if (currentTimers && currentTimers.length > 0) {
            for (let i = 0; i < currentTimers.length; i++) {
              const timer = currentTimers[i];

              if (timer > 0) {
                this._zones[i].currentTime = timer;
                this.setCache(
                  'zones',
                  this._zones,
                  CACHE_TIME,
                  undefined,
                  timestamp
                );
                this._onUpdate.next({ zones: this.zones });
                break;
              }
            }
          }
        }
      } else if (attribute.toLowerCase() === 'mode') {
        const mode: any = response.value;

        const programIndex = this.getProgramIdFromMode(mode);

        this._percentAdjust = this.getPercentAdjust(mode);
        this._isEnabled = this.getDeviceisEnabled(mode);

        this.state = {
          mode
        };

        this._programs = this._programs.map((item: any, index: number) => {
          item.isOn = index == programIndex;
          return item;
        });

        this.setCache(
          'programs',
          this.programs,
          CACHE_TIME,
          undefined,
          timestamp
        );
        this.setCache(
          'percentAdjust',
          this.percentAdjust,
          CACHE_TIME,
          undefined,
          timestamp
        );
        this.setCache(
          'isEnabled',
          this.isEnabled,
          CACHE_TIME,
          undefined,
          timestamp
        );

        this._onUpdate.next({
          isEnabled: this.isEnabled,
          percentAdjust: this.percentAdjust,
          programs: this.programs,
          state: this.state
        });
      } else if (attribute.toLowerCase() === 'arraysize') {
        this.updateArraySize(response.value);
        this.setArraySize(response.value);
      } else if (attribute.toLowerCase() === 'pumpmode') {
        this.updatePumpMode(response.value);
      }
    }

    return this.state;
  }

  public getNearestCondition(): Observable<any> {
    const programsIdxs = this.programs.map((p: any, idx: number) => {
      return idx;
    });

    const query = {
      'meta.deviceId': this.device._id,
      'meta.programId': { $in: programsIdxs }
    };

    return this.conditionsService
      .list(query, { fields: ['meta', 'description'] })
      .map((response) => {
        const conditions = response.data;
        const description = this.nearestConditionDescriptionHelper(conditions);
        return description;
      });
  }

  public getCrontabFromValue(value: any): any {
    const crontabStr = value[ConditionModel.CRONTAB] || '';
    const crontab = crontabStr.split(' ');
    const date = new Date();

    const minutes = parseInt(crontab[0]);
    const hours = parseInt(crontab[1]);

    date.setUTCMinutes(minutes);
    date.setUTCHours(hours);

    const minutesUtc = date.getMinutes();
    const hoursUtc = date.getHours();

    const day = parseInt(crontab[2]);

    const monthsValues = ['*'];

    const daysStr = crontab[4] || '';

    let daysValues = daysStr.split(',').reduce((prev: any, next: any) => {
      return next === '*' ? prev : prev.concat(next);
    }, []);

    const values = Object.assign({}, daysValues);

    daysValues = this.days.map((dayv: any) => {
      const enabledDay = values.find((dayValue: any) => {
        return parseInt(dayValue) === dayv.value;
      });

      dayv.enabled = false;
      if (enabledDay) {
        dayv.enabled = true;
      }
      return dayv;
    });

    return {
      day,
      days: daysValues,
      hours: hoursUtc,
      minutes: minutesUtc,
      months: monthsValues
    };
  }

  private nearestConditionDescriptionHelper(conditions: any) {
    const currentDate = new Date();
    const currentTime = +currentDate;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    // TODO get this from config
    const sunriseHour = 6;
    const sunsetHour = 18;
    let nearestDiff = Infinity;
    let date: any;
    let value: any;
    let description: any;

    if (conditions) {
      conditions.forEach((condition: any) => {
        if (condition.type === ConditionModel.TIME_DEFAULT) {
          value = this.getCrontabFromValue(condition.value);
          date = new Date(year, month, day, value.hours, value.minutes);
        } else if (value === ConditionModel.TIME_SUNRISE) {
          date = new Date(year, month, day, sunriseHour);
        } else {
          date = new Date(year, month, day, sunsetHour);
        }

        const diff = +date - currentTime;
        if (diff > 0 && diff < nearestDiff) {
          nearestDiff = diff;
          description = condition.description;
        }
      });
    }

    return description;
  }

  public createProgramAction(
    programName: string,
    programId: string,
    startTimes: any,
    crontab: any,
    actionId?: any,
    effectId?: any
  ): Observable<any> {
    const path = [
      '',
      'devices',
      this.device._id,
      'comps',
      this.componentId,
      'procs',
      this.processorName
    ].join('/');
    let createdAction: ActionModel;
    const currentTimerValues = this.programs[programId].timers || [];

    // savePrograms(currentTimerValues, programId, programName);

    const action: ActionModel = this.actionsService.createModel({
      expr: { if: [], then: [], else: [], operator: 'OR' },
      meta: { programId, deviceId: this.device._id },
      name: 'Rainbee program ' + programId + ' action'
    });

    // this is an action update
    if (actionId) {
      action._id = actionId;
    }

    let gateway: any;
    const gatewayQuery = { 'components.processors.name': 'TimeServer' };
    const gatewayProjection = { fields: ['name', 'components'], limit: 1 };

    return this.devicesService
      .list(gatewayQuery, gatewayProjection)
      .flatMap((gatewaysListResponse) => {
        const devices = gatewaysListResponse.data;
        gateway = devices[0];

        return action.save();
      })
      .flatMap((actionSaveResponse) => {
        createdAction = actionSaveResponse.data;

        if (!createdAction) {
          return Observable.throw(
            new Error('Error saving action for raindelay')
          );
        }
        action._id = createdAction._id;

        const gatewayProcessor = gateway.getProcessor('TimeServer');
        const gatewayPath = [
          '',
          'devices',
          gateway._id,
          'comps',
          gatewayProcessor.componentId,
          'procs',
          gatewayProcessor.name,
          'attrs',
          'Time'
        ].join('/');

        const conditions: any = [];

        for (let i = 0; i < startTimes.length; i++) {
          const time = startTimes[i];
          const value: any = {};

          value[ConditionModel.CRONTAB] = time.crontab ? time.crontab : crontab;

          if (time.type !== ConditionModel.TIME_DEFAULT) {
            value[ConditionModel.EQ] =
              time.type === ConditionModel.TIME_SUNRISE
                ? ConditionModel.SUNRISE
                : ConditionModel.SUNSET;
          }

          const meta = { programId, deviceId: this.device._id };
          const condition = this.conditionsService.createModel({
            meta,
            'name': 'RainBee Program ' + programId + ' Condition',
            'parents.actions': [
              { id: createdAction._id, name: createdAction.name }
            ],
            'path': gatewayPath,
            'type': time.type,
            value
          });

          if (time.id) {
            condition._id = time.id;
          }

          conditions.push(condition.save());
        }

        return Observable.forkJoin(conditions);
      })
      .flatMap((createdConditions: any) => {
        action.expr.if = [];
        for (let i = 0; i < createdConditions.length; i++) {
          let condition = createdConditions[i];
          condition = condition.data;
          if (condition) {
            action.expr.if.push({
              enabled: true,
              id: condition._id,
              name: condition.name
            });
          }
        }

        const effect = this.effectsService.createModel({
          'meta': { programId, deviceId: this.device._id },
          'name': 'RainBee Program ' + programId + ' Effect',
          'params': {
            ProgramID: programId
          },
          'parents.actions': [
            { id: createdAction._id, name: createdAction.name }
          ],
          'path': path + '/methods/ProgramOn'
        });

        if (effectId) {
          effect._id = effectId;
        }

        return effect.save();
      })
      .flatMap((createdEffect) => {
        createdEffect = createdEffect.data;

        if (!createdEffect) {
          Observable.throw(new Error('Error saving effect for raindelay'));
        }

        action.expr.then = [
          {
            enabled: true,
            id: createdEffect._id,
            name: createdEffect.name
          }
        ];

        return action.save();
      })
      .flatMap((updatedAction) => {
        return Observable.of(updatedAction);
      });
  }

  public getRainDelayAction(): Observable<any> {
    return this.actionsService
      .list({ 'meta.rainDelay': true, 'meta.deviceId': this.device._id })
      .map((actionResult) => actionResult.data[0]);
  }

  public stopRainDelay(): Observable<any> {
    const promises: any = [];
    let actionToRemove: ActionModel;

    this.enableDevice();

    if (this.device && this.device.meta && this.device.meta.rainDelay) {
      const conditionId = this.device.meta.rainDelay.conditionId;
      const effectId = this.device.meta.rainDelay.effectId;
      const effectToDeleteId = this.device.meta.rainDelay.effectToDeleteId;
      const actionId = this.device.meta.rainDelay.actionId;
      if (conditionId && effectToDeleteId && effectId) {
        promises.push(
          this.conditionsService.createModel({ _id: conditionId }).remove()
        );
        promises.push(
          this.effectsService.createModel({ _id: effectId }).remove()
        );
        promises.push(
          this.effectsService.createModel({ _id: effectToDeleteId }).remove()
        );
      }
      actionToRemove = this.actionsService.createModel({ _id: actionId });
    }

    if (!this.device.meta) {
      this.device.meta = {};
    }

    this.device.meta.rainDelay = null;
    promises.push(this.device.save());

    return Observable.forkJoin(promises)
      .flatMap((arrayResponse) => {
        // console.log('rain delay delete response', arrayResponse);

        return actionToRemove.remove();
      })
      .map((res) => {
        this._onRainDelayChange.next(false);
        return res;
      });
  }

  public startRainDelay(delay: any, timeZone: any): Observable<any> {
    let obsResponse: Observable<any>;
    if (!timeZone) {
      timeZone = '';
    }

    const mode = this.state.mode;
    const path = [
      '',
      'devices',
      this.device._id,
      'comps',
      this.componentId,
      'procs',
      this.processorName
    ].join('/');

    const action = this.actionsService.createModel({
      expr: { if: [], then: [], else: [], operator: 'OR' },
      meta: {
        delay,
        deviceId: this.device._id,
        rainDelay: true
      },
      name: 'RainBee Rain Delay action'
    });

    const time = new Date();

    if (delay !== 'inf') {
      // console.log('setting action.meta for timed');
      time.setTime(time.getTime() + delay * 60 * 60 * 1000);
      action.meta.startDate = new Date();
      action.meta.endDate = time;
    }

    let createdAction: ActionModel;
    let createdCondition: ConditionModel;
    let createdEffect: any;
    let createdEffectToDeleteAction: any;

    this.disableDevice();

    let gateway: DeviceModel;
    const gatewayQuery: any = { 'components.processors.name': 'TimeServer' };
    const gatewayProjection: any = { fields: ['name', 'components'], limit: 1 };

    const fullResponseL: any = {};
    if (delay === 'inf') {
      // console.log('saving action w/o a timer effect');
      obsResponse = action
        .save()
        .flatMap((actionsResponse: any) => {
          createdAction = action;
          if (!this.device.meta) {
            this.device.meta = {};
          }
          this.device.meta.rainDelay = {
            actionId: createdAction._id,
            delay: 'inf'
          };
          return this.device.save();
        })
        .flatMap((deviceResponse) => {
          return Observable.of(deviceResponse);
        });
    } else {
      obsResponse = this.devicesService
        .list(gatewayQuery, gatewayProjection)
        .flatMap((gatewayResponse: any) => {
          gateway = gatewayResponse.data[0];

          return action.save();
        })
        .flatMap((actionResponse: any) => {
          createdAction = action;

          const timeServerProcessor = gateway.getProcessor('TimeServer');

          const gatewayPath = [
            '',
            'devices',
            gateway._id,
            'comps',
            timeServerProcessor.componentId,
            'procs',
            timeServerProcessor.name,
            'attrs',
            'Time'
          ].join('/');

          createdCondition = this.conditionsService.createModel({
            'meta': { rainDelay: true },
            'name': 'Gateway triggers Rain Delay Condition',
            'parents.actions': [
              { id: createdAction._id, name: createdAction.name }
            ],
            'path': gatewayPath,
            'type': ConditionModel.TIME_DEFAULT,
            'value': {
              '#eq': time.toISOString(),
              '#tz': timeZone
            }
          });

          return createdCondition.save();
        })
        .flatMap((conditionResponse: any) => {
          action.expr.if.push({
            enabled: true,
            id: createdCondition._id,
            name: createdCondition.name
          });

          // Because we have inhibited the device
          // by the time the effect is running
          // the effect should be set inhibit off
          // to get the device operational again
          const newModeValue = this.inhibitOff(mode);

          createdEffect = this.effectsService.createModel({
            'meta': { rainDelay: true },
            'name': 'RainBee Rain Delay Effect',
            'params': { Mode: newModeValue },
            'parents.actions': [
              { id: createdAction._id, name: createdAction.name }
            ],
            'path': path + '/methods/setMode'
          });

          return createdEffect.save();
        })
        .flatMap((effectResponse: any) => {
          createdEffectToDeleteAction = this.effectsService.createModel({
            'name': 'RainBee Effect to Delete Action',
            'params': { verb: 'delete' },
            'parents.actions': [
              { id: createdAction._id, name: createdAction.name }
            ],
            'path': '/actions/' + createdAction._id
          });

          return createdEffectToDeleteAction.save();
        })
        .flatMap((deleteEffectResponse: any) => {
          action.expr.then.push(
            {
              enabled: true,
              id: createdEffect._id,
              name: createdEffect.name
            },
            {
              enabled: true,
              id: createdEffectToDeleteAction._id,
              name: createdEffectToDeleteAction.name
            }
          );

          return action.save();
        })
        .flatMap((actionUpdateResponse: any) => {
          if (!this.device.meta) {
            this.device.meta = {};
          }
          this.device.meta.rainDelay = {
            actionId: createdAction._id,
            conditionId: createdCondition._id,
            delay: time,
            effectId: createdEffect._id,
            effectToDeleteId: createdEffectToDeleteAction._id
          };

          return this.device.save();
        })
        .flatMap((deviceResponse: any) => {
          return Observable.of(deviceResponse);
        });
    }

    return obsResponse.map((res) => {
      this._onRainDelayChange.next(true);
      return res;
    });
  }

  public processDeviceMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;
    const response = message && message.data && message.data.response;

    if (attribute === 'OnOff' && message.componentId) {
      const zoneId = parseInt(message.componentId);

      this._zones[zoneId - 1].isOn =
        response.value === 'On' ||
        response.value === true ||
        response.value === 1;

      this.setCache('zones', this._zones, CACHE_TIME, undefined, timestamp);

      this._onUpdate.next({ zones: this.zones });
    }
  }

  public getProgramConditions(): Observable<any> {
    const programsIdxs = this.programs.map((p: any, idx: number) => {
      return idx + '';
    });

    const query = {
      'meta.deviceId': this.device._id,
      'meta.programId': { $in: programsIdxs }
    };

    return this.conditionsService.list(query, {
      fields: ['meta', 'description']
    });
  }

  public setPercentAdjust(percent: number) {
    this._percentAdjust = percent;

    percent = percent / 10;
    const mode = this.state.mode;
    const percentMask = 480;
    let newMode = mode & percentMask;
    newMode |= percent;
    const params = { value: newMode };

    this.device
      .setAttribute(this.componentId, this.processorName, 'Mode', params, false)
      .subscribe(() => {
        setTimeout(() => {
          this.readMode(true);
        }, SETTLE_TIME);
      });
  }

  public getProgramAutomation(programId: number) {
    const observables: Array<Observable<any>> = [];

    observables.push(
      this.actionsService.list(
        {
          'meta.deviceId': this.device._id,
          'meta.programId': programId
        },
        { limit: 1 }
      )
    );
    observables.push(
      this.conditionsService.list({
        'meta.deviceId': this.device._id,
        'meta.programId': programId
      })
    );
    observables.push(
      this.effectsService.list(
        {
          'meta.deviceId': this.device._id,
          'meta.programId': programId
        },
        { limit: 1 }
      )
    );

    return Observable.forkJoin(observables).flatMap((array) => {
      const automation: any = [];

      for (let i = 0; i < array.length; i++) {
        const res: any = array[i];
        if (res && res.data) {
          automation[i] = res.data;
        }
      }

      const response: any = {
        action: automation[0][0],
        conditions: automation[1],
        effect: automation[2][0]
      };

      return Observable.of(response);
    });
  }

  public getEffectLabelOptions(context: any) {
    let rsp: any = [];
    let attr: string = '';
    if (context && context.attribute) {
      attr = context.attribute;
    }

    if (attr == 'zones') {
      rsp = this._zones.map((zone: any, index: number) => {
        let name: string = zone.name;
        if (
          this.device.meta &&
          this.device.meta.zones &&
          this.device.meta.zones.length >= index
        ) {
          name = this.device.meta.zones[index].name;
        }
        return {
          name,
          payload: { ZoneID: index + 1 },
          value: String(index + 1)
        };
      });
      if (this._zonesLen > 0) {
        rsp = rsp.slice(0, this._zonesLen);
      }
    } else if (attr == 'programs') {
      rsp = this._programs.map((program: any, index: number) => {
        let name: string = program.name;
        if (
          this.device.meta &&
          this.device.meta.programs &&
          this.device.meta.programs.length >= index
        ) {
          name = this.device.meta.programs[index].name;
        }
        return {
          name,
          payload: { ProgramID: index },
          value: String(index)
        };
      });
      if (rsp.length > 1) {
        rsp = rsp.slice(1);
      }
    }

    return rsp;
  }

  public getEffectValueOptions(context: any) {
    let rsp: any = null;
    let attr: string = '';
    if (context && context.attribute) {
      attr = context.attribute;
    }

    if (attr == 'zones') {
      rsp = [
        {
          name: _.capitalize(
            _.get(
              this.translationService.getResource(),
              'AR.EFFECTS_NL.TURN_ON',
              ''
            )
          ),
          value: 'On'
        },
        {
          name: _.capitalize(
            _.get(
              this.translationService.getResource(),
              'AR.EFFECTS_NL.TURN_OFF',
              ''
            )
          ),
          value: 'Off'
        }
      ];
    } else if (attr == 'programs') {
      rsp = [
        {
          name: _.capitalize(
            _.get(
              this.translationService.getResource(),
              'AR.EFFECTS_NL.TURN_ON',
              ''
            )
          ),
          value: 'ProgramOn'
        },
        {
          name: _.capitalize(
            _.get(
              this.translationService.getResource(),
              'AR.EFFECTS_NL.TURN_OFF',
              ''
            )
          ),
          value: 'ProgramOff'
        }
      ];
    }

    return rsp;
  }

  public getEffectCurrentLabel(context: any, attributeOrMethod: string) {
    if (
      !context ||
      (!context.hasOwnProperty('ZoneID') &&
        !context.hasOwnProperty('ProgramID'))
    ) {
      return null;
    }
    const key = context.hasOwnProperty('ZoneID') ? 'ZoneID' : 'ProgramID';
    return context[key];
  }

  public getEffectCurrentValue(context: any, attributeOrMethod: string) {
    return attributeOrMethod;
  }

  public getEffectMethod(context?: any): string | null {
    if (!context || !context.name) {
      return null;
    }

    const name = context.name.toLowerCase();
    const currentMethod = context.method;

    if (name == 'zones' && ['On', 'Off'].indexOf(currentMethod) > -1) {
      return currentMethod;
    } else if (
      name == 'programs' &&
      ['ProgramOn', 'ProgramOff'].indexOf(currentMethod) > -1
    ) {
      return currentMethod;
    }

    return null;
  }

  public getValueDescription(value: any): string {
    let description = '';

    if (value.ZoneID) {
      const zone = this._zones[value.ZoneID - 1];
      if (zone) {
        description = zone.name;
        if (
          this.device.meta &&
          this.device.meta.zones &&
          this.device.meta.zones.length >= value.ZoneID - 1
        ) {
          description = this.device.meta.zones[value.ZoneID - 1].name;
        }
        description = `, ${_.get(
          this.translationService.getResource(),
          'AR.EFFECTS_NL.ZONE',
          ''
        )}: ${description}`;
      }
    } else if (value.ProgramID) {
      const program = this._programs[value.ProgramID];
      if (program) {
        description = program.name;
        if (
          this.device.meta &&
          this.device.meta.programs &&
          this.device.meta.programs.length >= value.ProgramID
        ) {
          description = this.device.meta.programs[value.ProgramID].name;
        }
        description = `, ${_.get(
          this.translationService.getResource(),
          'AR.EFFECTS_NL.PROGRAM',
          ''
        )}: ${description}`;
      }
    }

    return description;
  }
}
